'use client'

import type { NavItem } from '@/constants'
import type { Dispatch, SetStateAction } from 'react'

import { Accordion, Offcanvas } from 'react-bootstrap'
import parse from 'html-react-parser'
import { has } from 'lodash'

import right_arrow from '@/assets/images/right_arrow.svg'
import LoginLink from '@/components/headers/LoginLink'
import Link from '@/components/Link'

type MobileMenuItem = {
  id?: number
  title?: string
  parent?: string | string[]
  path?: string
  children?: NavItem[] | false
}

/**
 * The mobile off-canvas menu, lifted out of MainHeader verbatim.
 *
 * It renders `convertToMobileMenu`, which is derived from the same
 * `tabDesktopContent` the desktop mega-menu uses — that shared state is why
 * both stay owned by MainHeader and arrive here as explicit props.
 */
export function MobileMenu({
  isMenuOpen,
  toggleMenu,
  convertToMobileMenu,
  filteredTabItems,
  activeKey,
  setActiveKey,
  activeParent,
  setActiveParent,
  setTab,
  setTabDesktopContent,
  showLoginDropdown,
  router
}: {
  isMenuOpen: boolean
  toggleMenu: () => void
  convertToMobileMenu: MobileMenuItem[]
  /** tabItems with promotions injected and desktop-only entries filtered out. */
  filteredTabItems: (NavItem & { item?: NavItem[] })[]
  activeKey: string | null
  setActiveKey: Dispatch<SetStateAction<string | null>>
  activeParent: number | null
  setActiveParent: Dispatch<SetStateAction<number | null>>
  setTab: (tab: number) => void
  setTabDesktopContent: Dispatch<SetStateAction<NavItem[]>>
  showLoginDropdown: boolean
  router: { push: (href: string) => void }
}) {
  return (
    <Offcanvas id="mobile-nav" show={isMenuOpen} onHide={toggleMenu} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <LoginLink showLoginDropdown={showLoginDropdown} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Accordion defaultActiveKey="0" id="mobile-menu">
          <Accordion.Item
            eventKey="4"
            onClick={() => {
              router.push('/')
              toggleMenu()
            }}
          >
            <Accordion.Header style={{ lineHeight: '44px' }}>Home</Accordion.Header>
          </Accordion.Item>
          {filteredTabItems.map(category => (
            <Accordion.Item
              key={category.id}
              eventKey={String(category.id ?? '')}
              onClick={() => {
                setTabDesktopContent(category.item ?? [])
                setTab(category.id ?? 0)
              }}
            >
              <Accordion.Header>
                <span
                  className="d-inline-block w-100"
                  style={{ lineHeight: '44px' }}
                  onClick={e => {
                    e.stopPropagation()
                    if (category.path === undefined) return
                    router.push(category.path)
                    toggleMenu()
                  }}
                >
                  {parse(category.name ?? '')}
                </span>
                <img
                  width="18"
                  alt="Expand menu"
                  src={right_arrow.src}
                  className={`accordion-toggle d-inline-block align-top ${
                    activeParent === category.id ? 'rotate-90' : ''
                  }`}
                  onClick={e => {
                    setActiveParent(category.id ?? null)
                    setActiveKey('100')
                    ;(e.target as HTMLElement).classList.toggle('rotate-90')
                  }}
                />
              </Accordion.Header>

              <Accordion.Body>
                {convertToMobileMenu.map((menu, menuIdx) => (
                  <Accordion
                    key={menuIdx}
                    id="mobile-menu"
                    activeKey={activeKey}
                    defaultActiveKey={activeKey}
                    onSelect={key => setActiveKey(typeof key === 'string' ? key : null)}
                  >
                    <Accordion.Item eventKey={`${menu.id}00`} key={menu.id}>
                      <Accordion.Header>
                        <span
                          className="d-inline-block w-100"
                          style={{ lineHeight: '44px' }}
                          onClick={() => {
                            if (activeKey === `${menu.id}00`) {
                              setActiveKey(null)
                            } else {
                              setActiveKey(`${menu.id}00`)
                            }
                            if (menu.path === undefined) return
                            router.push(menu.path)
                            toggleMenu()
                          }}
                        >
                          {menu.title}
                        </span>
                        {menu.children && (
                          <img
                            width="18"
                            alt="Expand menu"
                            src={right_arrow.src}
                            className={`accordion-toggle d-inline-block align-top margin-right-5px ${
                              `${menu.id}00` === activeKey ? 'rotate-90' : ''
                            }`}
                            onClick={e => {
                              setActiveKey(`${menu.id}00`)
                              ;(e.target as HTMLElement).classList.toggle('rotate-90')
                            }}
                          />
                        )}
                      </Accordion.Header>
                      <Accordion.Body>
                        {Array.isArray(menu.children) &&
                          menu.children.map((child, childIdx) =>
                            has(child, 'children') ? (
                              <div key={childIdx}>
                                <div className="mobile-list list">
                                  <span
                                    className={
                                      child?.children &&
                                      `d-inline-flex justify-content-between w-100`
                                    }
                                  >
                                    {child?.title && parse(child?.title)}
                                    {child?.children && (
                                      <img
                                        width="18"
                                        alt="Expand menu"
                                        src={right_arrow.src}
                                        className="d-inline-block rotate-90 align-top margin-right-5px "
                                        style={{
                                          filter: 'grayscale(1)',
                                          opacity: 0.2
                                        }}
                                      />
                                    )}
                                  </span>
                                  {has(child, 'children') && (
                                    <div className="d-flex flex-column ps-4">
                                      {Array.isArray(child.children) &&
                                        child.children.map((grandChild, index) => (
                                          <Link
                                            href={grandChild.path || '#'}
                                            onClick={e => {
                                              if (!grandChild.path) e.preventDefault()
                                              toggleMenu()
                                            }}
                                            key={grandChild.title}
                                          >
                                            <div
                                              className={`mobile-list list pe-0 me-0
                                                ${index === 0 ? `mt-2` : ``}
                                                `}
                                            >
                                              <span
                                                style={{
                                                  textAlign: 'left',
                                                  color: 'var(--grey-500-body-text)'
                                                }}
                                              >
                                                {grandChild?.title && parse(grandChild?.title)}
                                              </span>
                                            </div>
                                          </Link>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <Link
                                key={childIdx}
                                href={child?.path || '#'}
                                onClick={e => {
                                  if (!child?.path) e.preventDefault()
                                  toggleMenu()
                                }}
                              >
                                <div className="mobile-list list">
                                  <span>{child?.title && parse(child?.title)}</span>
                                </div>
                              </Link>
                            )
                          )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          ))}
          <Accordion.Item
            eventKey="5"
            onClick={() => {
              router.push('/important-notices')
              toggleMenu()
            }}
          >
            <Accordion.Header style={{ lineHeight: '44px' }}>Important Notices</Accordion.Header>
          </Accordion.Item>
          <Accordion.Item
            eventKey="6"
            onClick={() => {
              router.push('/career')
              toggleMenu()
            }}
          >
            <Accordion.Header style={{ lineHeight: '44px' }}>Career</Accordion.Header>
          </Accordion.Item>
          <Accordion.Item
            eventKey="7"
            onClick={() => {
              router.push('/news')
              toggleMenu()
            }}
          >
            <Accordion.Header style={{ lineHeight: '44px' }}>News & Announcements</Accordion.Header>
          </Accordion.Item>
          <Accordion.Item
            eventKey="8"
            onClick={() => {
              router.push('/contact-us')
              toggleMenu()
            }}
          >
            <Accordion.Header style={{ lineHeight: '44px' }}>Contact Us</Accordion.Header>
          </Accordion.Item>

          <Accordion.Item
            eventKey="9"
            onClick={() => {
              window.location.replace('https://www.alrajhibank.com.sa')
              toggleMenu()
            }}
          >
            <Accordion.Header style={{ lineHeight: '44px' }}>Global Site</Accordion.Header>
          </Accordion.Item>
        </Accordion>
      </Offcanvas.Body>
    </Offcanvas>
  )
}
